

import React, { useState } from "react";
import { HighlightText, KpiIcon } from "./KpiIcons";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { PrimaryButton, SecondaryButton } from "../../../../globalComponents/ui/Button";
import { usePermission } from "../../../../hooks/usePermission";


/* ═══════════════════════════════════════════════════════════════
   AVATAR + CELL COMPONENTS
═══════════════════════════════════════════════════════════════ */
const AVATAR_COLORS = [
    "#2563eb", "#16a34a", "#dc2626", "#d97706",
    "#7c3aed", "#0891b2", "#be185d", "#0f766e", "#ea580c", "#4f46e5",
];
const avatarBg = (id) => AVATAR_COLORS[Number(id) % AVATAR_COLORS.length];

const UserAvatar = ({ user, size = 26, overlap = false }) => {
    const [hovered, setHovered] = useState(false);
    const initials = [user.first_name?.[0], user.last_name?.[0]].filter(Boolean).join("").toUpperCase() || "?";
    return (
        <div
            style={{ position: "relative", display: "inline-block", marginLeft: overlap ? -7 : 0, zIndex: hovered ? 10 : 1 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div style={{
                width: size, height: size, borderRadius: "50%",
                background: avatarBg(user.id), color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: size * 0.36, fontWeight: 700, fontFamily: "var(--font-sans)",
                border: "2px solid #fff", flexShrink: 0, cursor: "default", userSelect: "none",
            }}>{initials}</div>
            {hovered && (
                <div style={{
                    position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
                    transform: "translateX(-50%)",
                    background: "#1e293b", color: "#fff",
                    borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600,
                    fontFamily: "var(--font-sans)", whiteSpace: "nowrap", zIndex: 9999,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)", pointerEvents: "none",
                }}>
                    <div>{user.name}</div>
                    {user.email && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{user.email}</div>}
                    <div style={{ position: "absolute", top: "100%", left: "50%", transform: "translateX(-50%)", border: "5px solid transparent", borderTopColor: "#1e293b" }} />
                </div>
            )}
        </div>
    );
};

const UserDropdown = ({ users = [], open, onClose, anchorWidth = 260 }) => {
    const [query, setQuery] = useState("");
    const filtered = users.filter(u => u.name?.toLowerCase().includes(query.toLowerCase()));
    if (!open) return null;
    return (
        <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0,
            width: anchorWidth, background: "var(--surface)",
            border: "1.5px solid var(--rule)", borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", zIndex: 9999, padding: 8,
        }} onMouseLeave={onClose}>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search user…"
                style={{ width: "100%", padding: "6px 8px", fontSize: 11, borderRadius: 6, border: "1px solid var(--rule)", marginBottom: 6, fontFamily: "var(--font-sans)" }}
            />
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
                {filtered.length ? filtered.map(u => (
                    <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 6 }}>
                        <UserAvatar user={u} size={26} />
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name}</div>
                            {u.email && <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>}
                        </div>
                    </div>
                )) : (
                    <div style={{ padding: "12px 8px", fontSize: 11, color: "var(--ink-4)", textAlign: "center" }}>No users found</div>
                )}
            </div>
        </div>
    );
};

const AssignedToCell = ({ users, width = 150 }) => {
    const [open, setOpen] = useState(false);
    const list = Array.isArray(users) ? users : [];
    const MAX = 3;
    const visible = list.slice(0, MAX);
    const extra = list.length - MAX;
    if (!list.length) return (
        <div style={{ width, padding: "14px 10px" }}>
            <span style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}>Unassigned</span>
        </div>
    );
    return (
        <div style={{ width, padding: "12px 10px", position: "relative" }} onMouseLeave={() => setOpen(false)}>
            <div style={{ display: "flex", alignItems: "center" }}>
                {visible.map((u, i) => <UserAvatar key={u.id} user={u} overlap={i > 0} />)}
                {extra > 0 && (
                    <div onMouseEnter={() => setOpen(true)} style={{
                        width: 26, height: 26, borderRadius: "50%", background: "#f1f5f9",
                        border: "2px solid #fff", marginLeft: -7,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9.5, fontWeight: 700, color: "var(--ink-3)", cursor: "default",
                    }}>+{extra}</div>
                )}
            </div>
            <UserDropdown users={list} open={open} onClose={() => setOpen(false)} />
        </div>
    );
};

const AssignedByCell = ({ user, width = 130 }) => {
    const [open, setOpen] = useState(false);
    const list = Array.isArray(user) ? user : [];
    const MAX = 3;
    const visible = list.slice(0, MAX);
    const extra = list.length - MAX;
    if (!list.length) return (
        <div style={{ width, padding: "14px 10px" }}>
            <span style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}>Unassigned</span>
        </div>
    );
    return (
        <div style={{ width, padding: "12px 10px", position: "relative" }} onMouseLeave={() => setOpen(false)}>
            <div style={{ display: "flex", alignItems: "center" }}>
                {visible.map((u, i) => <UserAvatar key={u.id} user={u} overlap={i > 0} />)}
                {extra > 0 && (
                    <div onMouseEnter={() => setOpen(true)} style={{
                        width: 26, height: 26, borderRadius: "50%", background: "#f1f5f9",
                        border: "2px solid #fff", marginLeft: -7,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 9.5, fontWeight: 700, color: "var(--ink-3)", cursor: "default",
                    }}>+{extra}</div>
                )}
            </div>
            <UserDropdown users={list} open={open} onClose={() => setOpen(false)} />
        </div>
    );
};

const StatusCell = ({ item, width = 120 }) => {
    const { currentUserId } = useCurrentUser();
    const isAdmin = Number(currentUserId) === 1;

    const rawHistory = item.quantative_history || [];
    const history = rawHistory
    const total = history.length;
    const accepted = history.filter(ans => ans.status?.toLowerCase() === 'approved').length;

    let text;
    let color;
    let bg;

    if (total > 0) {
        if (accepted === total) {
            text = "Accepted";
            color = "#16a34a"; // green
            bg = "#dcfce7";
        } else {
            text = `${accepted} / ${total} Accepted`;
            color = "#d97706"; // amber
            bg = "#fef3c7";
        }
    } else {
        text = "Pending";
        color = "#d97706";
        bg = "#fef3c7";
    }

    return (
        <div style={{ width, flexShrink: 0, padding: "14px 10px", display: "flex", alignItems: "center" }}>
            <span style={{
                padding: "4px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                background: bg, color: color, fontFamily: "var(--font-sans)", whiteSpace: "nowrap"
            }}>
                {text}
            </span>
        </div>
    );
};

const ValueCell = ({ name, width = 120 }) => (
    <div style={{ width, flexShrink: 0, padding: "14px 10px", display: "flex", alignItems: "center", gap: 5 }}>
        {name ? (
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "var(--font-sans)" }}>
                {name}
            </span>
        ) : (
            <span style={{ fontSize: 11, color: "var(--ink-4)", fontStyle: "italic" }}>—</span>
        )}
    </div>
);

/* ═══════════════════════════════════════════════════════════════
   ACCORDION SUB-COMPONENTS
═══════════════════════════════════════════════════════════════ */

/* ── Stat card: matches screenshot card style exactly ── */
const StatCard = ({ label, value, unit, accent, bg }) => (
    <div style={{
        padding: "12px 14px",
        background: bg,
        borderRadius: 10,
        border: `1px solid ${accent}22`,
        minWidth: 0,
    }}>
        <div style={{
            fontSize: 9.5, fontWeight: 700, color: accent,
            textTransform: "uppercase", letterSpacing: "0.07em",
            marginBottom: 6, fontFamily: "var(--font-sans)",
        }}>{label}</div>
        <div style={{
            fontSize: 26, fontWeight: 800, color: "var(--ink)",
            fontFamily: "var(--font-mono)", letterSpacing: "-0.03em", lineHeight: 1,
        }}>{value ?? 0}</div>
        {unit && (
            <div style={{
                fontSize: 9.5, color: "var(--ink-4)", textTransform: "uppercase",
                letterSpacing: "0.08em", fontFamily: "var(--font-mono)", marginTop: 4,
            }}>{unit}</div>
        )}
    </div>
);

/* ── Spark bar ── */
const SparkBar = ({ value, max, color }) => {
    const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width 0.4s ease" }} />
            </div>
            <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--ink-4)", minWidth: 32, textAlign: "right" }}>
                {pct.toFixed(0)}%
            </span>
        </div>
    );
};

/* ── Delta badge ── */
const DeltaBadge = ({ current, previous }) => {
    if (current == null || previous == null || previous === 0) return null;
    let delta = current > previous
        ? ((previous - current) / current) * 100
        : ((current - previous) / previous) * 100;
    const up = delta >= 0;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 3,
            padding: "2px 7px", borderRadius: 20, fontSize: 10, fontWeight: 700,
            fontFamily: "var(--font-mono)",
            background: up ? "#dcfce7" : "#fee2e2",
            color: up ? "#16a34a" : "#dc2626",
        }}>
            {up ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
        </span>
    );
};

/* ── Compact emission / energy bar row — matches screenshot exactly ── */
const CompactTypeRow = ({ icon, label, currentVal, previousVal, unit, accentColor, bgColor, borderColor }) => {
    const cur = Number(currentVal) || 0;
    const prev = Number(previousVal) || 0;
    const max = Math.max(cur, prev, 1);

    return (
        <div style={{
            display: "flex", alignItems: "center", gap: 0,
            background: bgColor,
            borderRadius: 8,
            border: `1px solid ${borderColor}`,
            overflow: "hidden",
        }}>
            {/* Label badge */}
            <div style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "10px 14px",
                minWidth: 120, flexShrink: 0,
                borderRight: `1px solid ${borderColor}`,
            }}>
                <span style={{ fontSize: 14 }}>{icon}</span>
                <span style={{
                    fontSize: 10, fontWeight: 700, color: accentColor,
                    textTransform: "uppercase", letterSpacing: "0.07em",
                    fontFamily: "var(--font-sans)",
                }}>{label}</span>
            </div>

            {/* Current FY */}
            <div style={{ flex: 1, padding: "8px 16px", borderRight: `1px solid ${borderColor}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "var(--ink-3)", fontFamily: "var(--font-sans)" }}>Current FY</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--font-mono)", color: accentColor }}>
                        {cur} <span style={{ fontSize: 9.5, fontWeight: 400, color: "var(--ink-4)" }}>{unit}</span>
                    </span>
                </div>
                <SparkBar value={cur} max={max} color={accentColor} />
            </div>

            {/* Previous FY */}
            <div style={{ flex: 1, padding: "8px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, alignItems: "baseline" }}>
                    <span style={{ fontSize: 10, color: "var(--ink-3)", fontFamily: "var(--font-sans)" }}>Previous FY</span>
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--ink-3)" }}>
                        {prev} <span style={{ fontSize: 9.5, fontWeight: 400, color: "var(--ink-4)" }}>{unit}</span>
                    </span>
                </div>
                <SparkBar value={prev} max={max} color="var(--ink-4)" />
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   ACCORDION PANEL
═══════════════════════════════════════════════════════════════ */
const AccordionPanel = ({ item, onAdd, onChat, onAudit, onAssign, onHistory, emissionMode, hasPermission }) => {

    const { currentUserId, currentUser } = useCurrentUser();
    const { userCapabilities = [] } = usePermission();
    const isAdmin = userCapabilities.includes("*") ||
        (currentUserId && Number(currentUserId) === 1) ||
        (currentUser && currentUser.parent_id === null);
    const canAddAndEditRecords = isAdmin || (item?.assigned_to && item.assigned_to.some(user => user.id == currentUserId));
    const [tab, setTab] = useState("overview");

    const analytics = item.analytics || {};

    /* ── kpi_type ── */
    const kpiTypes = Array.isArray(item.kpi_type) ? item.kpi_type.map(t => t.toUpperCase()) : [];
    const hasEmission = kpiTypes.includes("EMISSION");
    const hasEnergy = kpiTypes.includes("ENERGY");
    const hasNOx = kpiTypes.includes("NOX");
    const hasSOx = kpiTypes.includes("SOX");
    const hasPPM = kpiTypes.includes("PPM");

    /* ── General values ── */
    const currentFY = Number(analytics.fy_value || 0);
    const previousFY = Number(analytics.previous_fy_value || 0);
    const currentSelected = Number(analytics.selected_date_range_value || 0);
    const previousSelected = Number(analytics.previous_selected_date_range_value || 0);
    const maxVal = Math.max(currentFY, previousFY, 1);

    /* ── Emission ── */
    const emissionCurrent = Number(analytics.fy_emission || 0);
    const emissionPrevious = Number(analytics.previous_fy_emission || 0);
    const emissionUnit = analytics.emission_unit || "kgCO2e";

    /* ── Energy ── */
    const energyCurrent = Number(analytics.fy_energy || 0);
    const energyPrevious = Number(analytics.previous_fy_energy || 0);
    const energyUnit = analytics.energy_unit || "GJ";

    /* ── NOx ── */
    const noxCurrent = Number(analytics.fy_nox || 0);
    const noxPrevious = Number(analytics.previous_fy_nox || 0);
    const noxUnit = "mg/Nm³";


    /* ── SOx ── */
    const soxCurrent = Number(analytics.fy_sox || 0);
    const soxPrevious = Number(analytics.previous_fy_sox || 0);
    const soxUnit = "mg/Nm³";


    /* ── PPM ── */
    const ppmCurrent = Number(analytics.fy_ppm || 0);
    const ppmPrevious = Number(analytics.previous_fy_ppm || 0);
    const ppmUnit = "mg/Nm³";

    const TABS = [{ key: "overview", label: "Overview" }];

    const OverviewTab = () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* ── 1. Stat cards ── */}
            {emissionMode && hasEmission && (
                <CompactTypeRow
                    icon="🌿"
                    label="Emission"
                    currentVal={emissionCurrent}
                    previousVal={emissionPrevious}
                    unit={emissionUnit}
                    accentColor="#15803d"
                    bgColor="#f0fdf4"
                    borderColor="#bbf7d0"
                />
            )}

            {/* ── 2. Energy compact row — second when emissionMode ── */}
            {emissionMode && hasEnergy && (
                <CompactTypeRow
                    icon="⚡"
                    label="Energy"
                    currentVal={energyCurrent}
                    previousVal={energyPrevious}
                    unit={energyUnit}
                    accentColor="#b45309"
                    bgColor="#fffbeb"
                    borderColor="#fde68a"
                />
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
                <StatCard label="Current FY (Total)" value={currentFY} unit={item.unit} accent="var(--accent)" bg="var(--accent-3)" />
                <StatCard label="Previous FY (Total)" value={previousFY} unit={item.unit} accent="var(--purple)" bg="var(--purple-2)" />
                <StatCard label="Current FY (Selected Range)" value={currentSelected} unit={item.unit} accent="var(--amber)" bg="var(--amber-2)" />
                <StatCard label="Previous FY (Selected Range)" value={previousSelected} unit={item.unit} accent="var(--red)" bg="var(--red-2)" />
            </div>

            {/* ── 2. YoY comparison ── */}
            <div style={{ padding: "12px 14px", background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--rule)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10, fontFamily: "var(--font-sans)" }}>
                    Year-over-Year Comparison
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {[
                        { label: "Current FY", val: currentFY, color: "var(--accent)" },
                        { label: "Previous FY", val: previousFY, color: "var(--purple)" },
                    ].map(({ label, val, color }) => (
                        <div key={label}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ fontSize: 11, fontWeight: 600 }}>{label}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--font-mono)", color }}>
                                    {val} {item.unit}
                                </span>
                            </div>
                            <SparkBar value={val} max={maxVal} color={color} />
                        </div>
                    ))}
                </div>
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10.5, color: "var(--ink-3)" }}>Change vs Previous FY:</span>
                    <DeltaBadge current={currentFY} previous={previousFY} />
                </div>
            </div>

            {/* ── 3. Emission compact row ── */}
            {/* ── 5. Emission compact row — only when NOT emissionMode (already shown above) ── */}
            {!emissionMode && hasEmission && (
                <CompactTypeRow
                    icon="🌿"
                    label="Emission"
                    currentVal={emissionCurrent}
                    previousVal={emissionPrevious}
                    unit={emissionUnit}
                    accentColor="#15803d"
                    bgColor="#f0fdf4"
                    borderColor="#bbf7d0"
                />
            )}

            {/* ── 6. Energy compact row — only when NOT emissionMode (already shown above) ── */}
            {!emissionMode && hasEnergy && (
                <CompactTypeRow
                    icon="⚡"
                    label="Energy"
                    currentVal={energyCurrent}
                    previousVal={energyPrevious}
                    unit={energyUnit}
                    accentColor="#b45309"
                    bgColor="#fffbeb"
                    borderColor="#fde68a"
                />
            )}

            {!emissionMode && hasNOx && (
                <CompactTypeRow
                    icon="💨"
                    label="NOx"
                    currentVal={noxCurrent}
                    previousVal={noxPrevious}
                    unit={noxUnit}
                    accentColor="#b91c1c"
                    bgColor="#fef2f2"
                    borderColor="#fecaca"
                />
            )}

            {!emissionMode && hasSOx && (
                <CompactTypeRow
                    icon="🟡"
                    label="SOx"
                    currentVal={soxCurrent}
                    previousVal={soxPrevious}
                    unit={soxUnit}
                    accentColor="#a16207"
                    bgColor="#fefce8"
                    borderColor="#fef08a"
                />
            )}

            {!emissionMode && hasPPM && (
                <CompactTypeRow
                    icon="🌫️"
                    label="PPM"
                    currentVal={ppmCurrent}
                    previousVal={ppmPrevious}
                    unit={ppmUnit}
                    accentColor="#6b21a8"
                    bgColor="#faf5ff"
                    borderColor="#e9d5ff"
                />
            )}

            {/* ── 5. Action buttons ── */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingTop: 14, borderTop: "1px solid var(--rule-2)" }}>
                {!emissionMode && hasPermission('quantative-data.create') && (
                    <PrimaryButton
                        style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }}
                        onClick={e => { e.stopPropagation(); onAdd?.(item); }}
                    >
                        <KpiIcon name="add" size={11} color="#fff" /> Add Data
                    </PrimaryButton>
                )}
                {hasPermission('quantative-data.view') && (
                    <SecondaryButton
                        style={{ height: 32, padding: "0 14px", fontSize: 12, minWidth: "auto" }}
                        onClick={e => { e.stopPropagation(); onHistory?.(item); }}
                    >
                        <KpiIcon name="history" size={11} /> History
                    </SecondaryButton>
                )}
            </div>
        </div>
    );

    return (
        <div style={{ padding: "16px 20px 20px", background: "linear-gradient(to bottom, #f8faff, #f4f6fe)", borderTop: "2px solid var(--accent-2)" }}>
            {/* Tab bar */}
            <div style={{ display: "flex", gap: 1, borderBottom: "2px solid var(--rule-2)", marginBottom: 16 }}>
                {TABS.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: "6px 14px", border: "none", background: "transparent", cursor: "pointer",
                        fontSize: 12, fontWeight: tab === t.key ? 700 : 500,
                        color: tab === t.key ? "var(--accent)" : "var(--ink-3)",
                        borderBottom: `2px solid ${tab === t.key ? "var(--accent)" : "transparent"}`,
                        marginBottom: -2,
                    }}>
                        {t.label}
                    </button>
                ))}
            </div>
            {tab === "overview" && <OverviewTab />}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN KpiRow
   Props:
     expanded  {boolean}  — controlled by parent (QuantativeList)
     onExpand  {function} — called when user clicks row to toggle
═══════════════════════════════════════════════════════════════ */
const KpiRow = ({
    item,
    index = 0,
    selected,
    onToggle,
    searchQuery = "",
    expanded = false,
    onExpand,
    onAdd,
    onChat,
    onAudit,
    onAssign,
    onHistory,
    isEven = false,
    emissionMode = false,
    hasPermission,
}) => {
    const baseBg = isEven ? "#ffffff" : "#f8fafd";

    const handleRowClick = (e) => {
        if (e.target.closest(".kpi-no-expand")) return;
        onExpand?.();
    };

    return (
        <div
            data-kpi-id={item.id}
            style={{
                borderBottom: "1.5px solid #dde2ea",
                background: expanded ? "#eef4ff" : selected ? "#e6eeff" : baseBg,
                transition: "background 0.15s ease",
                boxShadow: expanded ? "inset 3px 0 0 var(--accent)" : "none",
                marginBottom: "15px",
            }}
        >
            {/* ── MAIN ROW ── */}
            <div onClick={handleRowClick} style={{ display: "flex", alignItems: "stretch", cursor: "pointer", userSelect: "none" }}>

                {/* Checkbox */}
                <div className="kpi-no-expand" onClick={e => e.stopPropagation()}
                    style={{ width: 46, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 0" }}>
                    <input type="checkbox" className="kpi-checkbox" checked={selected} onChange={onToggle} disabled={emissionMode} />
                </div>

                {/* Row number */}
                <div style={{ width: 32, flexShrink: 0, display: "flex", alignItems: "flex-start", paddingTop: 18, fontSize: 12, fontWeight: 700, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
                    {index + 1}.
                </div>

                {/* KPI name */}
                <div style={{ flex: 1, minWidth: 0, padding: "14px 14px 14px 10px" }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: "var(--ink)", lineHeight: 1.5, fontFamily: "var(--font-sans)", letterSpacing: "-0.01em", wordBreak: "break-word" }}>
                        <HighlightText text={item.kpi_name} query={searchQuery} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 5 }} />
                </div>

                {/* Assigned To */}
                <AssignedToCell users={item.assigned_to} width={150} />

                {/* Assigned By */}
                <AssignedByCell user={item.assigned_by} width={130} />

                {/* Status */}
                <StatusCell item={item} width={120} />

                {/* Value */}
                <ValueCell
                    name={
                        emissionMode
                            ? (item.fy_emission != null
                                ? `${item.fy_emission}${item.emission_unit ? ` ${item.emission_unit}` : ""}`
                                : null)
                            : (item.value != null
                                ? `${item.value}${item.unit ? ` ${item.unit}` : ""}`
                                : null)
                    }
                    width={120}
                />

                {/* Chevron */}
                <div style={{ width: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{
                        width: 26, height: 26, borderRadius: "50%",
                        border: "1.5px solid var(--rule)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.2s ease",
                        background: expanded ? "var(--accent-3)" : "transparent",
                        borderColor: expanded ? "var(--accent-2)" : "var(--rule)",
                        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    }}>
                        <KpiIcon name="chevron" size={13} color={expanded ? "var(--accent)" : "var(--ink-4)"} />
                    </div>
                </div>
            </div>

            {/* ── ACCORDION PANEL ── */}
            {expanded && (
                <div className="kpi-slide-down">
                    <AccordionPanel
                        item={item}
                        onAdd={onAdd}
                        onChat={onChat}
                        onAudit={onAudit}
                        onAssign={onAssign}
                        onHistory={onHistory}
                        emissionMode={emissionMode}
                        hasPermission={hasPermission}
                    />
                </div>
            )}
        </div>
    );
};

export default KpiRow;